import { useEffect, useState } from 'react';
import useAnnotations from '../useAnnotations';
import useDistractors from '../useDistractor.service';
import useFacet from '../useFacet.service';
import hasDistractors from '../utils/hasDistractors';
import {
  flashcards,
  free_responses,
  instructional_contents,
  question_and_answers,
  ratings,
  regions,
  sequences,
  survey_question_and_answers,
  vocabulary,
} from './interactions';
import type {
  Flashcards,
  FreeResponses,
  InstructionalContent,
  QuestionAndAnswer,
  Ratings,
  Regions,
  Sequences,
  SurveyQuestion,
  Vocabulary,
} from './interactions';

export type Interaction =
  | QuestionAndAnswer
  | Sequences
  | Regions
  | InstructionalContent
  | SurveyQuestion
  | FreeResponses
  | Ratings
  | Flashcards // This type is used by code, but the templateType is 'associations'
  | Vocabulary
  | null;

const generateCompositeFnMap = {
  sequences,
  free_responses,
  survey_question_and_answers,
  ratings,
  question_and_answers,
  regions,
  instructional_contents,
  associations: flashcards,
  vocabulary,
};

export default function useItem(setId: string, selectedItem: any, itemsService: any) {
  const templateType = selectedItem?.templateType;
  const facetId = selectedItem?.facets?.[0]?.anchor?.id;

  const [item, setItem] = useState<Interaction>(null);
  const [idForDistractors, setIdForDistractors] = useState<string | undefined>(undefined);
  const [compositeItemCache, setCompositeItemCache] = useState<Interaction[]>([]);

  const distractors = useDistractors(idForDistractors);
  const annotations = useAnnotations(setId, selectedItem?.id);
  const facets = useFacet(setId, selectedItem?.id);

  useEffect(() => {
    const id = hasDistractors(templateType) ? facetId : undefined;
    setIdForDistractors(id);

    if (!selectedItem) {
      setItem(null);
    }
  }, [selectedItem]);

  useEffect(() => {
    if (selectedItem) {
      const hd = hasDistractors(templateType);
      const id = hd ? facetId : undefined;

      const itemFromStore = compositeItemCache.find((i) => i?.id === selectedItem?.id);
      if (itemFromStore) {
        setItem(itemFromStore);
      } else if (selectedItem && !distractors.isLoading && (!hd || distractors.id === id)) {
        // any item that has distractors should wait for the distractors hook to load and be updated
        const compositeFunction = generateCompositeFnMap[templateType];

        if (compositeFunction) {
          const props = {
            item: selectedItem,
            distractors,
            facets,
            annotations,
            setId,
            setItem,
            itemsService,
          };

          const compositeItem = compositeFunction(props) as Interaction;

          setCompositeItemCache((prev) => [...prev, compositeItem]);
          setItem(compositeItem);
        }
      }
    }
  }, [selectedItem, distractors.isLoading, distractors.id]);

  // keep store to avoid full recreation/rerender process and keep any open or half edited state
  useEffect(() => {
    const itemFromStore = compositeItemCache.find((i) => i?.id === item?.id);
    if (JSON.stringify(item) !== JSON.stringify(itemFromStore)) {
      setCompositeItemCache((prev) => prev.map((prevItem) => (prevItem?.id === item?.id ? item : prevItem)));
    }
  }, [item]);

  // used when creating a new item. as some require us to manually add an empty facet
  // not related to the rest of useItem just needed to put this somewhere
  // TODO find a better place to handle this in future
  const addEmptyFacetOnItemCreationIfNeeded = async (templateType: string, newItemId: string) => {
    const templatesWithEmptyDistractorOnCreation = ['question_and_answers', 'associations', 'vocabulary'];

    const templatesWithEmptyFacetOnCreation = templatesWithEmptyDistractorOnCreation.concat([
      'free_response',
      'survey_question_and_answers',
    ]);

    if (templatesWithEmptyFacetOnCreation.includes(templateType)) {
      const newFacet = await facets.create({
        pathParams: { id: newItemId },
        data: {
          item_id: newItemId,
          include: 'anchor,anchor.image,anchor.sound,association,association.image,association.sound',
        },
        queryParams: {},
      });

      if (templatesWithEmptyDistractorOnCreation.includes(templateType)) {
        await distractors.create({
          pathParams: { id: newFacet.anchor.id },
          data: {
            concept_id: Number(newFacet.anchor.id),
            include: 'image',
          },
        });
      }

      const attributes = {
        question_and_answers: { 'explanation-type': 'explain_item' },
        free_response: { 'answer-type': 'single_correct' },
      }[templateType];

      if (attributes) {
        await itemsService.update({
          data: { data: { attributes } },
          pathParams: { id: newItemId },
        });
      }

      await distractors.update({
        pathParams: { id: newFacet.anchor.id },
        data: {
          data: {
            attributes: {
              'text-html': ' ',
            },
          },
        },
      });

      // needs to return the new facets so that questions and answers can load the first time
      // it nees to the id from facets to find the distractors on first load
      return newFacet;
    }
  };

  return { item, addEmptyFacetOnItemCreationIfNeeded };
}
